<!--
  LwMenu: the row-action menu (refresh_plan.md section 6, web paper cut 2:
  "Row action menu items are mouse-only `<div @click>`").

  Two defects this fixes at once (audit_web.md 3.4, the `cMenu` /
  `cMenuAction` passage): every row action in `cMenuAction.vue:2` is a
  `<div class="c-menu-action" @click>` with `display: table-row`, so a
  keyboard user cannot act on a menu item even though the opener
  (`cTableActionsMenu.vue:3`) is a real, focusable `<button>`. That same
  opener button has no text content and no `aria-label`, so a screen reader
  announces it as an empty button: a keyboard user can open a menu they
  cannot then use, and cannot even tell what the button they just activated
  was for. This component makes both ends real: a labelled opener button and
  real `<button role="menuitem">` items.

  Follows LwDialog's house pattern: a module-level id counter for stable,
  distinct ids per instance, and a single document-level listener pair
  (keydown, click) added on open and torn down on close and on
  beforeUnmount, so two menus open across a session cannot leave two live
  handlers behind.

  This phase builds the component only. No caller (`cMenu` / `cMenuAction` /
  `cTableActionsMenu`) is migrated yet.
-->
<template>
  <div ref="root" class="lw-c-menu">
    <button
      :id="triggerId"
      ref="trigger"
      type="button"
      class="lw-c-menu__trigger"
      aria-haspopup="menu"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-controls="menuId"
      :aria-label="label"
      @click="toggleTrigger"
      @keydown="handleTriggerKeydown"
    >
      <span v-if="triggerLabel" class="lw-c-menu__trigger-label">{{ triggerLabel }}</span>
      <!--
        Icon-only appearance when triggerLabel is null (contract): the glyph
        is decoration, aria-hidden, because aria-label above already supplies
        the accessible name. This is the exact control audit_web.md 3.4 found
        with neither: this element never renders without one.
      -->
      <i v-else class="fa fa-ellipsis-v lw-c-menu__trigger-glyph" aria-hidden="true" />
    </button>

    <!--
      POSITIONED IN JS, and fixed rather than absolute, because an absolutely
      positioned menu is clipped by any ancestor with `overflow: hidden` --
      and LwTable has exactly that on its wrapper, to keep the header's square
      corners inside the wrapper's rounded ones. Every row-action menu on the
      dispatch board and both billing tables lives inside that wrapper, so a
      menu with more items than the table is tall had its last items cut off
      with no scrollbar and no clue: on Billing that hid "Mark as billed" and
      "Delete invoice" entirely, which are the two the page exists for.

      z-index cannot fix this. An ancestor's overflow clips a descendant
      whatever its stacking order, and `position: fixed` is what takes the
      element out of that ancestor's clipping context.
    -->
    <div
      v-if="open"
      :id="menuId"
      ref="list"
      class="lw-c-menu__list"
      :class="alignClass"
      :style="menuStyle"
      role="menu"
      :aria-labelledby="triggerId"
    >
      <button
        v-for="(item, index) in items"
        :key="item.id != null ? item.id : index"
        ref="itemButtons"
        type="button"
        role="menuitem"
        class="lw-c-menu__item"
        :class="{ 'lw-c-menu__item--destructive': item.destructive }"
        :disabled="item.disabled"
        @click="selectItem(item)"
      >
        <i
          v-if="item.iconBefore"
          class="fa lw-c-menu__item-icon"
          :class="item.iconBefore"
          aria-hidden="true"
        />
        {{ item.label }}
      </button>
    </div>
  </div>
</template>

<script>
// Module-level, the same shape as LwDialog's dialogIdCounter: every instance
// gets a distinct, stable id pair for aria-labelledby / aria-controls,
// generated once per instance rather than per open/close cycle.
let menuIdCounter = 0;
function nextMenuId() {
  menuIdCounter += 1;
  return menuIdCounter;
}

const ALIGNS = ['start', 'end'];

export default {
  name: 'LwMenu',
  props: {
    // { id?, label, onSelect, disabled?, destructive?, iconBefore? }. label
    // is required text because there is no icon-only item: the opener this
    // component replaces already proves what an unlabelled control announces
    // as (contract). onSelect takes no arguments; the caller closes over its
    // own row, so this component never has to map an index back to one.
    items: {
      type: Array,
      required: true,
      validator: value =>
        value.every(item => typeof item.label === 'string' && typeof item.onSelect === 'function'),
    },
    // The opener's accessible name. A prop rather than a constant because
    // "Actions for load 4021" is a better name than "Row actions", and the
    // caller is the only one who knows the row (contract).
    label: {
      type: String,
      default: 'Row actions',
    },
    // Visible text on the opener. Null keeps it icon-only in appearance,
    // still carrying `label` as its accessible name via aria-label below.
    triggerLabel: {
      type: String,
      default: null,
    },
    align: {
      type: String,
      default: 'end',
      validator: value => ALIGNS.includes(value),
    },
  },
  emits: ['open', 'close'],
  data() {
    return {
      generatedId: nextMenuId(),
      // Inline top/left, written by positionMenu() once the list has been
      // rendered and can be measured. Null while closed.
      menuStyle: null,
      open: false,
    };
  },
  computed: {
    triggerId() {
      return `lw-menu-trigger-${this.generatedId}`;
    },
    menuId() {
      return `lw-menu-${this.generatedId}`;
    },
    alignClass() {
      return `lw-c-menu__list--${this.align}`;
    },
  },
  beforeUnmount() {
    // Mirrors LwDialog's teardown-on-unmount: a menu destroyed while open
    // must not strand a live document listener behind (contract).
    this.removeDocumentListeners();
  },
  methods: {
    toggleTrigger() {
      if (this.open) {
        this.closeMenu();
      } else {
        this.openMenu('first');
      }
    },
    // ArrowDown / ArrowUp on the closed opener open the menu and focus the
    // first or last enabled item (contract). Once open, this handler steps
    // aside: the document-level listener added by openMenu owns the keys
    // from here, because focus has moved off the trigger and onto an item.
    handleTriggerKeydown(event) {
      if (this.open) return;
      const key = event.key;
      if (key === 'ArrowDown' || event.keyCode === 40) {
        event.preventDefault();
        // Stops this same event from also reaching the document keydown
        // listener that openMenu is about to attach: without this, the event
        // (already bubbling toward document) would still visit the newly
        // added listener during this same dispatch and be handled twice.
        event.stopPropagation();
        this.openMenu('first');
      } else if (key === 'ArrowUp' || event.keyCode === 38) {
        event.preventDefault();
        event.stopPropagation();
        this.openMenu('last');
      }
    },
    openMenu(focusTarget) {
      if (this.open) return;
      this.open = true;
      this.addDocumentListeners();
      this.$emit('open');
      this.$nextTick(() => {
        // Position BEFORE focusing: focusing an item can scroll it into view,
        // and doing that while the menu is still at its unpositioned origin
        // makes the page jump.
        this.positionMenu();
        const index = focusTarget === 'last' ? this.lastEnabledIndex() : this.firstEnabledIndex();
        this.focusItem(index);
      });
    },
    closeMenu(options = {}) {
      const focusTrigger = options.focusTrigger === true;
      if (!this.open) return;
      this.open = false;
      this.menuStyle = null;
      this.removeDocumentListeners();
      this.$emit('close');
      // The trigger is never v-if'd away, so this is safe to call
      // synchronously; unlike focusing a freshly-rendered item, there is no
      // patch to wait a tick for (contract: "returns focus to the opener").
      if (focusTrigger && this.$refs.trigger) this.$refs.trigger.focus();
    },
    /**
     * Place the open menu against its trigger, in viewport coordinates.
     *
     * The list is `position: fixed` (see the template comment), so it is
     * positioned relative to the viewport and nothing an ancestor does to
     * overflow can clip it. The cost of that is this method: fixed elements
     * do not follow their trigger, so the coordinates are recomputed on open
     * and on every scroll or resize while open.
     *
     * FLIPS UPWARD rather than being cut off. A row-action menu on the last
     * row of a table is the normal case, not an edge case, and a menu that
     * runs off the bottom of the window is the same failure this whole change
     * is fixing, just one level out. It only flips when there is actually
     * more room above, and clamps to the viewport either way, so a menu taller
     * than the window still shows its top rather than sliding off both ends.
     */
    positionMenu() {
      const trigger = this.$refs.trigger;
      const list = this.$refs.list;
      // jsdom reports zeroes for every rect, so this is a no-op under test
      // (and harmless: the assertions there are about structure, not pixels).
      if (!trigger || !list || typeof window === 'undefined') return;

      const t = trigger.getBoundingClientRect();
      const l = list.getBoundingClientRect();
      // Matches the --lw-space-1 gap the absolute version carried as
      // margin-top, and the same "geometry, not a spacing role" exception the
      // stylesheet below already claims for it.
      const GAP = 4;
      // Keeps the shelf off the very edge of the window when it is clamped.
      const EDGE = 8;

      let top = t.bottom + GAP;
      const roomBelow = window.innerHeight - t.bottom - GAP - EDGE;
      const roomAbove = t.top - GAP - EDGE;
      if (l.height > roomBelow && roomAbove > roomBelow) top = t.top - GAP - l.height;
      top = Math.max(EDGE, Math.min(top, window.innerHeight - l.height - EDGE));

      // `align` still decides which edge of the trigger the menu hangs from;
      // it is just resolved here now instead of by a left/right declaration,
      // because those cannot be clamped to the viewport.
      let left = this.align === 'end' ? t.right - l.width : t.left;
      left = Math.max(EDGE, Math.min(left, window.innerWidth - l.width - EDGE));

      this.menuStyle = { top: `${Math.round(top)}px`, left: `${Math.round(left)}px` };
    },
    addDocumentListeners() {
      if (typeof document === 'undefined') return;
      if (this._keydownHandler) return;
      this._keydownHandler = this.handleDocumentKeydown;
      this._clickHandler = this.handleDocumentClick;
      document.addEventListener('keydown', this._keydownHandler);
      document.addEventListener('click', this._clickHandler);
      // A fixed element does not move with its trigger, so it has to be told.
      // Scroll is captured because the thing that scrolls is usually an
      // ancestor (the page, a table wrapper), and scroll does not bubble.
      this._repositionHandler = this.positionMenu;
      window.addEventListener('scroll', this._repositionHandler, true);
      window.addEventListener('resize', this._repositionHandler);
    },
    removeDocumentListeners() {
      if (typeof document === 'undefined') return;
      if (this._keydownHandler) {
        document.removeEventListener('keydown', this._keydownHandler);
        this._keydownHandler = null;
      }
      if (this._clickHandler) {
        document.removeEventListener('click', this._clickHandler);
        this._clickHandler = null;
      }
      if (this._repositionHandler) {
        window.removeEventListener('scroll', this._repositionHandler, true);
        window.removeEventListener('resize', this._repositionHandler);
        this._repositionHandler = null;
      }
    },
    // A click is only "outside" when it lands outside the root element,
    // trigger included: a click on the trigger while open is handled by
    // toggleTrigger's own listener first (element listeners run before an
    // ancestor's, so the menu is already closed and this listener already
    // removed by the time the same click would reach document).
    handleDocumentClick(event) {
      const root = this.$refs.root;
      if (!root || root.contains(event.target)) return;
      this.closeMenu();
    },
    handleDocumentKeydown(event) {
      const key = event.key;
      if (key === 'Escape' || event.keyCode === 27) {
        event.preventDefault();
        this.closeMenu({ focusTrigger: true });
        return;
      }
      // A menu is not a tab stop (contract): close and let the key do its
      // own default thing rather than preventing it or trapping focus the
      // way LwDialog's dialog does.
      if (key === 'Tab' || event.keyCode === 9) {
        this.closeMenu();
        return;
      }
      if (key === 'ArrowDown' || event.keyCode === 40) {
        event.preventDefault();
        this.focusRelative(1);
        return;
      }
      if (key === 'ArrowUp' || event.keyCode === 38) {
        event.preventDefault();
        this.focusRelative(-1);
        return;
      }
      if (key === 'Home' || event.keyCode === 36) {
        event.preventDefault();
        this.focusItem(this.firstEnabledIndex());
        return;
      }
      if (key === 'End' || event.keyCode === 35) {
        event.preventDefault();
        this.focusItem(this.lastEnabledIndex());
        return;
      }
      if (key === 'Enter' || key === ' ' || event.keyCode === 13 || event.keyCode === 32) {
        const index = this.focusedItemIndex();
        if (index === -1) return;
        // Prevents the native click a real <button> would otherwise
        // synthesize for this same Enter/Space press, which would call
        // selectItem a second time (contract: "exactly once").
        event.preventDefault();
        this.selectItem(this.items[index]);
      }
    },
    selectItem(item) {
      if (!item || item.disabled) return;
      item.onSelect();
      this.closeMenu({ focusTrigger: true });
    },
    itemElements() {
      const refs = this.$refs.itemButtons;
      if (!refs) return [];
      return Array.isArray(refs) ? refs : [refs];
    },
    enabledIndices() {
      const indices = [];
      this.items.forEach((item, index) => {
        if (!item.disabled) indices.push(index);
      });
      return indices;
    },
    firstEnabledIndex() {
      const enabled = this.enabledIndices();
      return enabled.length ? enabled[0] : -1;
    },
    lastEnabledIndex() {
      const enabled = this.enabledIndices();
      return enabled.length ? enabled[enabled.length - 1] : -1;
    },
    focusedItemIndex() {
      return this.itemElements().indexOf(document.activeElement);
    },
    // Moves by `direction` (1 or -1) among enabled items only, wrapping at
    // either end (contract). Working in the enabled-only index space, then
    // mapping back to the real item index, is what makes a disabled item
    // invisible to Arrow navigation without any special-casing at the call
    // sites below.
    focusRelative(direction) {
      const enabled = this.enabledIndices();
      if (enabled.length === 0) return;
      const currentIndex = this.focusedItemIndex();
      const posInEnabled = enabled.indexOf(currentIndex);
      let nextPos;
      if (posInEnabled === -1) {
        nextPos = direction > 0 ? 0 : enabled.length - 1;
      } else {
        nextPos = (posInEnabled + direction + enabled.length) % enabled.length;
      }
      this.focusItem(enabled[nextPos]);
    },
    focusItem(index) {
      if (index === -1) return;
      const el = this.itemElements()[index];
      if (el && typeof el.focus === 'function') el.focus();
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../../sass/mixins.generated';

.lw-c-menu {
  position: relative;
  display: inline-block;
}

.lw-c-menu__trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--lw-space-2);
  border: 0;
  border-radius: var(--lw-radius-sm);
  background-color: transparent;
  color: var(--lw-fg-secondary);
  cursor: pointer;
  padding: var(--lw-space-2);
  font: var(--lw-type-button);
  letter-spacing: var(--lw-type-button-tracking);
  text-transform: var(--lw-type-button-transform);
  transition: background-color var(--lw-motion-fast) var(--lw-motion-ease);

  @include lw-focus-visible;
}

.lw-c-menu__trigger:hover {
  background-color: var(--lw-bg-inset);
  color: var(--lw-fg-primary);
}

.lw-c-menu__trigger-glyph {
  // Font Awesome glyphs inherit size from the surrounding text (same as
  // LwButton's icon); nothing to size here.
  line-height: 1;
}

.lw-c-menu__list {
  // FIXED, not absolute, and placed by positionMenu(). An absolutely
  // positioned menu is clipped by any ancestor with `overflow: hidden`, which
  // LwTable's wrapper has -- see the template comment for what that cost on
  // the billing page. top/left are supplied inline; these two are the
  // pre-measurement origin, overwritten on the same tick.
  position: fixed;
  top: 0;
  left: 0;
  // No token models a menu's minimum shelf width: a raw px floor
  // keeps a one-word item ("Cancel load") from collapsing to its own text
  // width. Geometry only, per the same exception.
  min-width: 200px;
  z-index: var(--lw-z-overlay);
  display: flex;
  flex-direction: column;
  gap: var(--lw-space-1);
  padding: var(--lw-space-1);
  // The menu/popover elevation level (contract).
  background: var(--lw-elev-3-surface);
  box-shadow: var(--lw-elev-3-shadow);
  border: 1px solid var(--lw-elev-3-line);
  border-radius: var(--lw-radius-md);
}

// NOTE: `.lw-c-menu__list--start` / `--end` carry no declarations any more.
// positionMenu() resolves `align` into an inline `left`, so the result can
// also be clamped to the viewport, which a static left/right cannot be. The
// classes stay on the element as the rendered record of which edge was asked
// for, and the component's tests assert them.

.lw-c-menu__item {
  display: flex;
  align-items: center;
  gap: var(--lw-space-2);
  width: 100%;
  box-sizing: border-box;
  border: 0;
  border-radius: var(--lw-radius-sm);
  background-color: transparent;
  color: var(--lw-fg-primary);
  font: var(--lw-type-body);
  text-align: left;
  cursor: pointer;
  padding: var(--lw-space-2) var(--lw-space-3);
  transition: background-color var(--lw-motion-fast) var(--lw-motion-ease);

  @include lw-focus-visible;
}

.lw-c-menu__item:hover:not(:disabled) {
  background-color: var(--lw-bg-inset);
}

// Destructive is never colour alone (contract): the item's own wording
// ("Cancel load") already says what it does; the danger tokens are the
// second, reinforcing signal, not the only one.
.lw-c-menu__item--destructive {
  color: var(--lw-danger-fg);
}

.lw-c-menu__item--destructive:hover:not(:disabled) {
  background-color: var(--lw-danger-bg);
}

// A token colour pair, never opacity over a fill: opacity over a fill is
// exactly how the driver app reached a 2.5:1 disabled contrast
// (audit_mobile.md 3.10, contract). Ordered last so it wins over the
// destructive modifier above when an item is both.
.lw-c-menu__item:disabled {
  color: var(--lw-fg-disabled);
  background-color: var(--lw-bg-inset);
  cursor: not-allowed;
}

.lw-c-menu__item-icon {
  line-height: 1;
}
</style>
